// src/pages/ResourcesPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaFilter, 
  FaSearch, 
  FaTimes, 
  FaBook, 
  FaDownload, 
  FaVideo,
  FaNewspaper,
  FaFileAlt,
  FaCalendarAlt,
  FaEye,
  FaArrowRight,
  FaLock,
  FaCheck,
  FaGraduationCap,
  FaUser
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import Card from '../components/common/Card';

const ResourcesPage = () => {
  // State for filtering and search
  const [activeTab, setActiveTab] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResources, setFilteredResources] = useState([]);
  
  // Mock resources data (in a real app, this would come from an API)
  const resourcesData = {
    blogs: [
      {
        id: 1,
        title: "The Future of Digital Skills in Sierra Leone",
        excerpt: "Exploring how digital skills are transforming employment prospects for youth in Sierra Leone and what skills will be most in demand in the coming years.",
        category: "career",
        type: "blog",
        coverImage: "/images/resources/blog-digital-skills.jpg",
        author: "Ing. Alfred Mbayoh",
        date: "2025-03-20",
        readTime: "8 min read",
        views: 432,
        featured: true,
        tags: ["digital skills", "employment", "youth", "technology"]
      },
      {
        id: 2,
        title: "Building Information Modeling: Revolutionizing Construction in West Africa",
        excerpt: "How BIM technology is changing the face of construction projects in West Africa, improving efficiency, reducing costs, and enhancing collaboration.",
        category: "technical",
        type: "blog",
        coverImage: "/images/resources/blog-bim.jpg",
        author: "Mohamed Sylvanus Kamara",
        date: "2025-02-15",
        readTime: "6 min read",
        views: 285,
        featured: false,
        tags: ["BIM", "construction", "technology", "engineering"]
      },
      {
        id: 3,
        title: "From Trainee to Professional: Success Stories from Afrovate Tech Hub Alumni",
        excerpt: "Highlighting the journeys of several Afrovate Tech Hub graduates who have successfully transitioned into professional careers using their digital skills.",
        category: "success-stories",
        type: "blog",
        coverImage: "/images/resources/blog-success.jpg",
        author: "Bailor Bah",
        date: "2025-01-28",
        readTime: "10 min read",
        views: 514,
        featured: true,
        tags: ["success stories", "alumni", "career growth", "professional development"]
      },
      {
        id: 4,
        title: "Drone Technology for Land Surveying: A Practical Guide",
        excerpt: "An introduction to using drone technology for land surveying applications, including equipment selection, regulations, and basic techniques.",
        category: "technical",
        type: "blog",
        coverImage: "/images/resources/blog-drone.jpg",
        author: "Mohamed Fornah",
        date: "2024-12-10",
        readTime: "12 min read",
        views: 376,
        featured: false,
        tags: ["drones", "surveying", "technology", "land management"]
      }
    ],
    downloads: [
      {
        id: 5,
        title: "AutoCAD Essentials Cheat Sheet",
        description: "A quick reference guide covering essential AutoCAD commands and shortcuts for beginners.",
        category: "technical",
        type: "download",
        format: "PDF",
        fileSize: "2.4 MB",
        coverImage: "/images/resources/download-autocad.jpg",
        downloadUrl: "/downloads/autocad-cheatsheet.pdf",
        date: "2025-02-10",
        downloads: 728,
        featured: true,
        restricted: false,
        tags: ["AutoCAD", "cheat sheet", "reference", "beginners"]
      },
      {
        id: 6,
        title: "Digital Skills Roadmap for Engineering Students",
        description: "A comprehensive guide to developing key digital skills throughout an engineering education.",
        category: "career",
        type: "download",
        format: "PDF",
        fileSize: "4.1 MB",
        coverImage: "/images/resources/download-roadmap.jpg",
        downloadUrl: "/downloads/digital-skills-roadmap.pdf",
        date: "2025-01-05",
        downloads: 543,
        featured: false,
        restricted: false,
        tags: ["career planning", "education", "engineering", "skill development"]
      },
      {
        id: 7,
        title: "BIM Project Template Pack",
        description: "A collection of Revit and Chief Architect templates to jumpstart your BIM projects.",
        category: "technical",
        type: "download",
        format: "ZIP",
        fileSize: "156 MB",
        coverImage: "/images/resources/download-bim.jpg",
        downloadUrl: "/downloads/bim-templates.zip",
        date: "2024-12-15",
        downloads: 342,
        featured: false,
        restricted: true,
        tags: ["BIM", "Revit", "Chief Architect", "templates"]
      },
      {
        id: 8,
        title: "Python for Engineers: Getting Started Guide",
        description: "An introduction to Python programming specifically tailored for engineering applications.",
        category: "technical",
        type: "download",
        format: "PDF",
        fileSize: "3.7 MB",
        coverImage: "/images/resources/download-python.jpg",
        downloadUrl: "/downloads/python-for-engineers.pdf",
        date: "2024-11-20",
        downloads: 612,
        featured: true,
        restricted: false,
        tags: ["Python", "programming", "engineering", "beginners"]
      }
    ],
    videos: [
      {
        id: 9,
        title: "Introduction to Revit Architecture",
        description: "A beginner-friendly introduction to Revit Architecture, covering the interface, basic modeling, and project setup.",
        category: "technical",
        type: "video",
        duration: "32:15",
        coverImage: "/images/resources/video-revit.jpg",
        videoUrl: "https://www.youtube.com/embed/TXH8wiHE0dM",
        date: "2025-03-05",
        views: 845,
        featured: true,
        tags: ["Revit", "tutorial", "BIM", "architecture"]
      },
      {
        id: 10,
        title: "Career Talk: Digital Skills in the Engineering Industry",
        description: "A panel discussion with industry professionals about the most valuable digital skills for engineers today.",
        category: "career",
        type: "video",
        duration: "48:30",
        coverImage: "/images/resources/video-career.jpg",
        videoUrl: "https://www.youtube.com/embed/TXH8wiHE0dM",
        date: "2025-02-20",
        views: 632,
        featured: false,
        tags: ["career talk", "panel discussion", "industry insights", "professional development"]
      },
      {
        id: 11,
        title: "Global Mapper Tutorial: Basic Land Surveying",
        description: "Learn how to use Global Mapper for basic land surveying tasks, including importing data and creating contour maps.",
        category: "technical",
        type: "video",
        duration: "25:40",
        coverImage: "/images/resources/video-mapping.jpg",
        videoUrl: "https://www.youtube.com/embed/TXH8wiHE0dM",
        date: "2025-01-15",
        views: 528,
        featured: false,
        tags: ["Global Mapper", "tutorial", "surveying", "mapping"]
      },
      {
        id: 12,
        title: "Afrovate Tech Hub Cohort 12 Graduation Ceremony Highlights",
        description: "Highlights from the Afrovate Tech Hub Cohort 12 Graduation Ceremony, including speeches and student testimonials.",
        category: "events",
        type: "video",
        duration: "18:22",
        coverImage: "/images/resources/video-graduation.jpg",
        videoUrl: "https://www.youtube.com/embed/TXH8wiHE0dM",
        date: "2024-12-20",
        views: 476,
        featured: true,
        tags: ["graduation", "ceremony", "cohort 12", "testimonials"]
      }
    ]
  };
  
  // Categories for filtering
  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'technical', name: 'Technical Guides' },
    { id: 'career', name: 'Career Development' },
    { id: 'success-stories', name: 'Success Stories' },
    { id: 'events', name: 'Events' }
  ];
  
  // Combine all resources
  const allResources = [
    ...resourcesData.blogs,
    ...resourcesData.downloads,
    ...resourcesData.videos
  ];
  
  // Filter resources based on active tab, category, and search term
  useEffect(() => {
    let filtered = [];
    
    // Filter by type (tab)
    if (activeTab === 'all') {
      filtered = allResources;
    } else if (activeTab === 'blogs') {
      filtered = resourcesData.blogs;
    } else if (activeTab === 'downloads') {
      filtered = resourcesData.downloads;
    } else if (activeTab === 'videos') {
      filtered = resourcesData.videos;
    }
    
    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === activeCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(resource => 
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Set filtered resources
    setFilteredResources(filtered);
  }, [activeTab, activeCategory, searchTerm, allResources, resourcesData]);
  
  // Reset to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  // Get featured resources
  const featuredResources = allResources.filter(resource => resource.featured);
  
  return (
    <>
      <Helmet>
        <title>Resources - Afrovate Tech Hub</title>
        <meta name="description" content="Access free resources, tutorials, blog posts, and downloadable materials to enhance your digital skills." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/resources/resources-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Resources</span>
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Resources & Learning Materials
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white text-opacity-90 mb-8"
            >
              Access our collection of blogs, tutorials, downloadable materials, and videos
              to enhance your digital skills and stay updated with industry trends.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative max-w-xl mx-auto"
            >
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 px-6 pr-12 bg-white bg-opacity-95 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                {searchTerm ? (
                  <button onClick={() => setSearchTerm('')} className="hover:text-primary">
                    <FaTimes />
                  </button>
                ) : (
                  <FaSearch />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Tab Navigation */}
      <div className="bg-white border-t border-b border-gray-200 sticky top-16 z-20">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap justify-between items-center gap-4">
            {/* Type Tabs */}
            <div className="flex overflow-x-auto scrollbar-hide">
              <button
                className={`whitespace-nowrap px-4 py-2 border-b-2 ${
                  activeTab === 'all'
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('all')}
              >
                All Resources
              </button>
              <button
                className={`whitespace-nowrap px-4 py-2 border-b-2 ${
                  activeTab === 'blogs'
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('blogs')}
              >
                Blog Posts
              </button>
              <button
                className={`whitespace-nowrap px-4 py-2 border-b-2 ${
                  activeTab === 'downloads'
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('downloads')}
              >
                Downloads
              </button>
              <button
                className={`whitespace-nowrap px-4 py-2 border-b-2 ${
                  activeTab === 'videos'
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('videos')}
              >
                Videos
              </button>
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center">
              <div className="flex items-center text-gray-700 mr-3">
                <FaFilter className="mr-2" /> Category:
              </div>
              <select
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-700 py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Clear Filters */}
            {(activeTab !== 'all' || activeCategory !== 'all' || searchTerm) && (
              <button
                onClick={() => {
                  setActiveTab('all');
                  setActiveCategory('all');
                  setSearchTerm('');
                }}
                className="text-primary font-medium hover:text-primary-dark flex items-center"
              >
                <FaTimes className="mr-2" /> Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Featured Resources */}
      {featuredResources.length > 0 && activeTab === 'all' && !searchTerm && activeCategory === 'all' && (
        <Section background="white" padding="lg">
          <div className="text-center mb-12">
            <Heading level={2} align="center" className="mb-4" withAccent>
              Featured Resources
            </Heading>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our top picks - hand-selected resources to help you develop your skills
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredResources.slice(0, 3).map((resource) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resource.coverImage}
                    alt={resource.title}
                    className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/800x400/1a237e/ffffff?text=${resource.title.replace(/ /g, '+')}`;
                    }}
                  />
                  <div className="absolute top-0 left-0 bg-primary-dark text-white px-3 py-1 m-3 rounded-md font-medium text-sm">
                    {resource.type === 'blog' && 'Blog Post'}
                    {resource.type === 'download' && 'Download'}
                    {resource.type === 'video' && 'Video'}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary-dark mb-3 line-clamp-2">{resource.title}</h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {resource.description || resource.excerpt}
                  </p>
                  
                  <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <FaCalendarAlt className="mr-1" />
                      <span>{formatDate(resource.date)}</span>
                    </div>
                    <div className="flex items-center">
                      {resource.type === 'blog' && (
                        <>
                          <FaEye className="mr-1" />
                          <span>{resource.views} views</span>
                        </>
                      )}
                      {resource.type === 'download' && (
                        <>
                          <FaDownload className="mr-1" />
                          <span>{resource.downloads} downloads</span>
                        </>
                      )}
                      {resource.type === 'video' && (
                        <>
                          <FaVideo className="mr-1" />
                          <span>{resource.duration}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {resource.type === 'blog' && (
                    <Link
                      to={`/blog/${resource.id}`}
                      className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                    >
                      Read Article <FaArrowRight className="ml-2" />
                    </Link>
                  )}
                  
                  {resource.type === 'download' && (
                    <a
                      href={resource.downloadUrl}
                      className={`inline-flex items-center font-medium ${
                        resource.restricted 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-primary hover:text-primary-dark'
                      }`}
                      onClick={(e) => {
                        if (resource.restricted) {
                          e.preventDefault();
                          alert('This resource is restricted to registered users. Please sign in or register.');
                        }
                      }}
                    >
                      {resource.restricted ? (
                        <>
                          <FaLock className="mr-2" /> Login to Download
                        </>
                      ) : (
                        <>
                          Download <FaDownload className="ml-2" />
                        </>
                      )}
                    </a>
                  )}
                  
                  {resource.type === 'video' && (
                    <a
                      href={resource.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                    >
                      Watch Video <FaArrowRight className="ml-2" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}
      
      {/* Resources List */}
      <Section background="gray" padding="lg">
        <div className="mb-8">
          <Heading level={2}>
            {(() => {
              if (activeTab === 'all') return 'All Resources';
              if (activeTab === 'blogs') return 'Blog Posts';
              if (activeTab === 'downloads') return 'Downloads';
              if (activeTab === 'videos') return 'Videos';
              return 'Resources';
            })()}
          </Heading>
          <p className="text-gray-600">
            {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''} found
            {searchTerm && ` matching "${searchTerm}"`}
            {activeCategory !== 'all' && ` in ${categories.find(c => c.id === activeCategory)?.name}`}
          </p>
        </div>
        
        {filteredResources.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="text-gray-400 text-5xl mb-4">
              <FaSearch className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Resources Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any resources matching your criteria.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setActiveTab('all');
                setActiveCategory('all');
                setSearchTerm('');
              }}
            >
              View All Resources
            </Button>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredResources.map((resource) => (
              <motion.div
                key={resource.id}
                variants={itemVariants}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resource.coverImage}
                    alt={resource.title}
                    className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/800x400/1a237e/ffffff?text=${resource.title.replace(/ /g, '+')}`;
                    }}
                  />
                  <div className="absolute top-0 left-0 bg-primary-dark text-white px-3 py-1 m-3 rounded-md font-medium text-sm">
                    {resource.type === 'blog' && 'Blog Post'}
                    {resource.type === 'download' && `Download • ${resource.format}`}
                    {resource.type === 'video' && `Video • ${resource.duration}`}
                  </div>
                  
                  <div className="absolute top-0 right-0 bg-secondary text-white px-3 py-1 m-3 rounded-md font-medium text-sm">
                    {categories.find(c => c.id === resource.category)?.name.split(' ')[0]}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary-dark mb-3 line-clamp-2">{resource.title}</h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {resource.description || resource.excerpt}
                  </p>
                  
                  <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <FaCalendarAlt className="mr-1" />
                      <span>{formatDate(resource.date)}</span>
                    </div>
                    <div className="flex items-center">
                      {resource.type === 'blog' && (
                        <>
                          <FaEye className="mr-1" />
                          <span>{resource.views} views</span>
                        </>
                      )}
                      {resource.type === 'download' && (
                        <>
                          <FaDownload className="mr-1" />
                          <span>{resource.downloads} downloads</span>
                        </>
                      )}
                      {resource.type === 'video' && (
                        <>
                          <FaVideo className="mr-1" />
                          <span>{resource.duration}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags.slice(0, 3).map((tag, index) => (
                      <span 
                        key={index}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {resource.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{resource.tags.length - 3} more</span>
                    )}
                  </div>
                  
                  {resource.type === 'blog' && (
                    <Link
                      to={`/blog/${resource.id}`}
                      className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                    >
                      Read Article <FaArrowRight className="ml-2" />
                    </Link>
                  )}
                  
                  {resource.type === 'download' && (
                    <a
                      href={resource.downloadUrl}
                      className={`inline-flex items-center font-medium ${
                        resource.restricted 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-primary hover:text-primary-dark'
                      }`}
                      onClick={(e) => {
                        if (resource.restricted) {
                          e.preventDefault();
                          alert('This resource is restricted to registered users. Please sign in or register.');
                        }
                      }}
                    >
                      {resource.restricted ? (
                        <>
                          <FaLock className="mr-2" /> Login to Download
                        </>
                      ) : (
                        <>
                          Download ({resource.format}, {resource.fileSize}) <FaDownload className="ml-2" />
                        </>
                      )}
                    </a>
                  )}
                  
                  {resource.type === 'video' && (
                    <a
                      href={resource.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                    >
                      Watch Video <FaArrowRight className="ml-2" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Section>
      
      {/* Newsletter Section */}
      <Section background="white" padding="lg">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 border-t-4 border-secondary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <Heading level={2} className="mb-4">
                  Stay Updated
                </Heading>
                <p className="text-gray-600 mb-6">
                  Subscribe to our newsletter to receive regular updates about new resources, 
                  blog posts, and upcoming events. Be the first to know when we publish new content.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Monthly digest of new resources</span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Exclusive content for subscribers</span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Early access to new programs</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-primary-dark text-xl mb-4">Join Our Newsletter</h3>
                <form>
                  <div className="mb-4">
                    <label htmlFor="newsletter-name" className="block mb-2 text-sm font-medium text-gray-700">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="newsletter-name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="newsletter-email" className="block mb-2 text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="newsletter-email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center">
                      <input
                        id="newsletter-terms"
                        type="checkbox"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="newsletter-terms" className="ml-2 text-sm text-gray-700">
                        I agree to receive email newsletters from Afrovate Tech Hub
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    variant="secondary" 
                    type="submit"
                    className="w-full"
                  >
                    Subscribe
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        </div>
      </Section>
      
      {/* Resource Categories */}
      <Section background="gray" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Resource Categories
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our resources by category to find exactly what you're looking for
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: "Technical Guides",
              icon: <FaBook className="h-10 w-10 text-white" />,
              description: "Step-by-step tutorials and guides for software and digital tools",
              color: "bg-blue-500",
              count: allResources.filter(r => r.category === 'technical').length
            },
            {
              title: "Career Development",
              icon: <FaGraduationCap className="h-10 w-10 text-white" />,
              description: "Resources to help advance your professional career",
              color: "bg-green-500",
              count: allResources.filter(r => r.category === 'career').length
            },
            {
              title: "Success Stories",
              icon: <FaUser className="h-10 w-10 text-white" />,
              description: "Inspiring stories from our alumni and their achievements",
              color: "bg-purple-500",
              count: allResources.filter(r => r.category === 'success-stories').length
            },
            {
              title: "Event Resources",
              icon: <FaCalendarAlt className="h-10 w-10 text-white" />,
              description: "Materials and recordings from past Afrovate Tech Hub events",
              color: "bg-yellow-500",
              count: allResources.filter(r => r.category === 'events').length
            }
          ].map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
            >
              <div className={`${category.color} p-6 flex justify-center`}>
                <div className="h-20 w-20 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  {category.icon}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary-dark mb-2">{category.title}</h3>
                <p className="text-gray-600 mb-4">{category.description}</p>
                <div className="text-sm text-gray-500 mb-4">{category.count} resources available</div>
                
                <button
                  onClick={() => {
                    setActiveCategory(index === 0 ? 'technical' : 
                                     index === 1 ? 'career' : 
                                     index === 2 ? 'success-stories' : 'events');
                    setActiveTab('all');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
                >
                  Browse {category.title}
                  <FaArrowRight className="ml-2 h-3 w-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>
      
      {/* Resource Type Sections */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
          <div>
            <Heading level={2} className="mb-6" withAccent>
              Resource Types
            </Heading>
            <p className="text-gray-700 mb-6">
              Our resources come in different formats to suit your learning style and needs. 
              From in-depth blog posts to downloadable guides and video tutorials, we've got you covered.
            </p>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <FaNewspaper className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary-dark mb-1">Blog Posts</h4>
                  <p className="text-gray-600">In-depth articles on industry trends, tutorials, and insights from our experts.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <FaFileAlt className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary-dark mb-1">Downloadable Materials</h4>
                  <p className="text-gray-600">Templates, cheat sheets, guides, and other resources you can download and use.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <FaVideo className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary-dark mb-1">Video Tutorials</h4>
                  <p className="text-gray-600">Step-by-step video guides and recordings of workshops and events.</p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <Button 
                variant="primary" 
                onClick={() => {
                  setActiveTab('blogs');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Browse Blogs
              </Button>
              
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  setActiveTab('downloads');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                View Downloads
              </Button>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg p-8">
              <Heading level={3} className="mb-6">
                Latest Blog Posts
              </Heading>
              
              <div className="space-y-6">
                {resourcesData.blogs.slice(0, 3).map((blog) => (
                  <div key={blog.id} className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-lg shadow-sm">
                    <div className="md:w-1/3 flex-shrink-0">
                      <img
                        src={blog.coverImage}
                        alt={blog.title}
                        className="w-full h-32 object-cover rounded-md"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/400x200/1a237e/ffffff?text=${blog.title.replace(/ /g, '+')}`;
                        }}
                      />
                    </div>
                    <div className="md:w-2/3">
                      <h4 className="font-bold text-primary-dark mb-1">{blog.title}</h4>
                      <p className="text-gray-600 text-sm mb-2 line-clamp-2">{blog.excerpt}</p>
                      <div className="flex justify-between text-xs text-gray-500 mb-3">
                        <span>{formatDate(blog.date)}</span>
                        <span>{blog.readTime}</span>
                      </div>
                      <Link
                        to={`/blog/${blog.id}`}
                        className="text-sm inline-flex items-center text-primary font-medium hover:text-primary-dark"
                      >
                        Read Full Article <FaArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-6">
                <Button 
                  variant="outline-primary" 
                  onClick={() => {
                    setActiveTab('blogs');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  View All Blog Posts
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Ready to Enhance Your Skills?
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Our resources are designed to complement our training programs. Take the next step
            and enroll in one of our courses to deepen your digital skills.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/programs"
            >
              Explore Programs
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/apply"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default ResourcesPage;